import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Input, InputNumber, Button, Modal, Spin, Select,
  Table, Tag, Tabs, DatePicker, Checkbox, Alert, Timeline, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { usePurchaseForm, Mode } from './hooks/usePurchaseForm';
import type { FD110, FD111, ApprovalEvent } from '../../services/d201Api';
import { d201Api } from '../../services/d201Api';
import { lookupApi, type LookupResult } from '../../services/lookupApi';
import SCSelectModal from './components/SCSelectModal';
import PrintOptionsModal from './components/PrintOptionsModal';
import { SharedActionBar } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwd201f1AcceptanceManifest } from '../../features/acceptance';
import { useModulePermission } from '../../hooks/usePermission';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import './D201Form.css';

/** Reusable lookup Select — fetches options from /api/lookup/:type on mount + search */
const LookupSelect: React.FC<{
  lookupType: string; value: string; disabled: boolean; label: string;
  contractFieldId: string;
  onChange: (code: string) => void; onNameResolved?: (name: string) => void;
}> = ({ lookupType, value, disabled, label, contractFieldId, onChange, onNameResolved }) => {
  const [options, setOptions] = useState<LookupResult[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await lookupApi.search(lookupType, keyword, 1, 50);
      setOptions(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [lookupType]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  return (
    <div>
      <label data-contract-field={contractFieldId} className="block text-xs text-gray-600 mb-1">{label}</label>
      <Select
        showSearch allowClear disabled={disabled} loading={loading}
        value={value || undefined} placeholder={`選擇${label}`}
        style={{ width: '100%' }}
        filterOption={false}
        onSearch={v => fetchOptions(v)}
        onChange={v => {
          onChange(v || '');
          const item = options.find(o => o.code === v);
          onNameResolved?.(item?.name || '');
        }}
        options={options.map(o => ({ value: o.code, label: `${o.code} - ${o.name}` }))}
      />
    </div>
  );
};

const { TextArea } = Input;

const dateToInput = (v: string | null | undefined): dayjs.Dayjs | null => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};
const fmt = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0.00';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const statusColor = (s: string): string => {
  switch (s) { case 'P': return 'green'; case 'D': return 'blue'; case 'N': return 'red'; default: return 'default'; }
};

// Reusable lookup field (code input + name display)
const LkField: React.FC<{
  label: string; value: string; name: string; disabled: boolean; maxLen?: number;
  contractFieldId: string;
  onChange: (v: string) => void; onBlur: () => void;
}> = ({ label, value, name, disabled, maxLen, contractFieldId, onChange, onBlur }) => (
  <div>
    <label data-contract-field={contractFieldId} className="block text-xs text-gray-600 mb-1">{label}</label>
    <div className="flex gap-1 items-center">
      <Input value={value} disabled={disabled} onChange={e => onChange(e.target.value)}
        onBlur={onBlur} maxLength={maxLen || 10} style={{ flex: 1 }} />
      <span className="text-xs text-gray-500 truncate" style={{ maxWidth: 120 }}>{name}</span>
    </div>
  </div>
);

const D201Form: React.FC = () => {
  const {
    mode, isEditing, loading, records, current, currentIdx,
    formData, details, lookupNames, updateField, updateDetailField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, statusLabel,
    addDetailRow, deleteDetailRow, resolveSingleName, ntdTotal, PURCHASE_TYPE_OPTIONS,
    showSCModal, setShowSCModal, importFromSC,
    getFieldError, handleSubmit, handleApprove, handleReject,
    lastApiResponse, setLastApiResponse,
  } = usePurchaseForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewNo = searchParams.get('review');
  const [searchText, setSearchText] = useState('');
  const { canEdit, canApprove } = useModulePermission('pwd201f1');

  // Selected detail row for sub-fields display
  const [selectedDetailIdx, setSelectedDetailIdx] = useState(0);
  const selectedDetail = details[selectedDetailIdx] || {};

  // History modal state
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<ApprovalEvent[]>([]);

  // Print options modal state
  const [printModalVisible, setPrintModalVisible] = useState(false);

  const handlePrint = () => {
    if (!current?.fd110_01) return;
    setPrintModalVisible(true);
  };

  const handleShowHistory = async () => {
    if (!current?.fd110_01) return;
    setHistoryVisible(true);
    setHistoryLoading(true);
    try {
      const events = await d201Api.getHistory(current.fd110_01);
      setHistoryEvents(events);
    } catch {
      message.error('無法載入審核歷史');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除採購單「${current.fd110_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: doDelete,
    });
  };

  const handleSubmitClick = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定送審此採購單「${current.fd110_01}」？`,
      okText: '送審',
      cancelText: '取消',
      onOk: handleSubmit,
    });
  };

  const handleApproveClick = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定核准此採購單「${current.fd110_01}」？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        const ok = await handleApprove();
        if (ok && reviewNo) navigate('/erp/approvals');
      },
    });
  };

  const handleRejectClick = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定退回此採購單「${current.fd110_01}」？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const ok = await handleReject();
        if (ok && reviewNo) navigate('/erp/approvals');
      },
    });
  };

  const handleWriteoffClick = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${current.fd110_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d201Api.writeoff(current.fd110_01);
          setLastApiResponse(resp);
          message.success(resp.message || '沖銷成功');
          window.location.reload();
        } catch (err: any) {
          const msg = err?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  const detailColumns: ColumnsType<Partial<FD111>> = [
    { title: <span data-contract-field="fd111_02">項次</span>, dataIndex: 'fd111_02', key: 'seq', width: 55 },
    { title: <span data-contract-field="fd111_10">產品大類</span>, dataIndex: 'fd111_10', key: 'c1', width: 80,
      render: (v: string, _: Partial<FD111>, i: number) => isEditing
        ? <Input value={v||''} size="small" onChange={e => updateDetailField(i,'fd111_10',e.target.value)} />
        : (v||'') },
    { title: <span data-contract-field="fd111_11">產品子類</span>, dataIndex: 'fd111_11', key: 'c2', width: 80,
      render: (v: string, _: Partial<FD111>, i: number) => isEditing
        ? <Input value={v||''} size="small" onChange={e => updateDetailField(i,'fd111_11',e.target.value)} />
        : (v||'') },
    { title: <span data-contract-field="fd111_12">產品名稱</span>, dataIndex: 'fd111_12', key: 'nm', ellipsis: true,
      render: (v: string, _: Partial<FD111>, i: number) => isEditing
        ? <Input value={v||''} size="small" onChange={e => updateDetailField(i,'fd111_12',e.target.value)} />
        : (v||'') },
    { title: <span data-contract-field="fd111_13">S/C NO</span>, dataIndex: 'fd111_13', key: 'sc', width: 90,
      render: (v: string, _: Partial<FD111>, i: number) => isEditing
        ? <Input value={v||''} size="small" onChange={e => updateDetailField(i,'fd111_13',e.target.value)} />
        : (v||'') },
    { title: <span data-contract-field="fd111_15">數量</span>, dataIndex: 'fd111_15', key: 'qty', width: 85,
      render: (v: number, _: Partial<FD111>, i: number) => isEditing
        ? <InputNumber value={v} min={0} size="small" style={{width:'100%'}} onChange={n => updateDetailField(i,'fd111_15',n??0)} />
        : (v??0) },
    { title: <span data-contract-field="fd111_16">單位</span>, dataIndex: 'fd111_16', key: 'u', width: 55,
      render: (v: string, _: Partial<FD111>, i: number) => isEditing
        ? <Input value={v||''} size="small" maxLength={4} onChange={e => updateDetailField(i,'fd111_16',e.target.value)} />
        : (v||'') },
    { title: <span data-contract-field="fd111_17">單價</span>, dataIndex: 'fd111_17', key: 'pr', width: 95,
      render: (v: number, _: Partial<FD111>, i: number) => isEditing
        ? <InputNumber value={v} min={0} step={0.01} size="small" style={{width:'100%'}} onChange={n => updateDetailField(i,'fd111_17',n??0)} />
        : fmt(v) },
    { title: <span data-contract-field="fd111_18">金額</span>, dataIndex: 'fd111_18', key: 'am', width: 100, render: (v: number) => fmt(v) },
    { title: <span data-contract-field="fd111_19">預定交期</span>, dataIndex: 'fd111_19', key: 'dd', width: 110,
      render: (v: string|null, _: Partial<FD111>, i: number) => isEditing
        ? <DatePicker value={dateToInput(v)} size="small" style={{width:'100%'}}
            onChange={(_d,ds) => updateDetailField(i,'fd111_19',ds?`${ds}T00:00:00Z`:null)} />
        : (v ? dayjs(v).format('YYYY-MM-DD') : '') },
    { title: <span data-contract-field="fd111_14">S/C項次</span>, dataIndex: 'fd111_14', key: 'sci', width: 65,
      render: (v: number, _: Partial<FD111>, i: number) => isEditing
        ? <InputNumber value={v} min={0} size="small" style={{width:'100%'}} onChange={n => updateDetailField(i,'fd111_14',n??0)} />
        : (v??0) },
    { title: <span data-contract-field="fd111_26">應付單價</span>, dataIndex: 'fd111_26', key: 'pp', width: 90, render: (v: number) => fmt(v) },
    { title: <span data-contract-field="fd111_25">折讓單價</span>, dataIndex: 'fd111_25', key: 'dp', width: 90,
      render: (v: number, _: Partial<FD111>, i: number) => isEditing
        ? <InputNumber value={v} min={0} step={0.001} size="small" style={{width:'100%'}} onChange={n => updateDetailField(i,'fd111_25',n??0)} />
        : fmt(v) },
    { title: <span data-contract-field="fd111_40">包缸費</span>, dataIndex: 'fd111_40', key: 'bf', width: 95,
      render: (v: number, _: Partial<FD111>, i: number) => isEditing
        ? <InputNumber value={v} min={0} step={0.01} size="small" style={{width:'100%'}} onChange={n => updateDetailField(i,'fd111_40',n??0)} />
        : fmt(v) },
    ...(isEditing ? [{ title: '操作', key: 'act', width: 55,
      render: (_:unknown, __:Partial<FD111>, i:number) =>
        <Button size="small" danger type="link" onClick={() => deleteDetailRow(i)}>刪除</Button>,
    }] : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-6xl mx-auto p-4">
        {reviewNo && (
          <Alert
            message="您正在審核此單據"
            description={`單號: ${reviewNo}。核准或退回後將自動返回待審核清單。`}
            type="warning"
            showIcon
            banner
            style={{ marginBottom: 12 }}
          />
        )}
        <h2 className="text-xl font-bold mb-3">
          採購單建檔 (D201)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx+1} / ${records.length}` : '無資料'}
            {current && <Tag className="ml-2" color={statusColor(current.fd110_80)}>{statusLabel(current.fd110_80)}</Tag>}
          </span>
        </h2>
        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入單號或供應商查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)} disabled={isEditing} style={{width:260}} maxLength={40} />
          <Button onClick={() => searchText.trim() && window.location.reload()} disabled={isEditing}>查詢</Button>
        </div>
        <Tabs defaultActiveKey="1" items={[
          { key:'1', label:'基本資料', children: renderTab1(formData,isEditing,lookupNames,updateField,resolveSingleName,PURCHASE_TYPE_OPTIONS,mode,getFieldError) },
          { key:'2', label:'產品明細', children: (
            <div>
              {isEditing && <div className="mb-2"><Button size="small" type="dashed" onClick={addDetailRow}>+ 新增明細行</Button></div>}
              <Table<Partial<FD111>> dataSource={details} columns={detailColumns}
                rowKey={r => `${r.fd111_01||'n'}-${r.fd111_02||0}`} size="small" pagination={false} scroll={{y:360}}
                onRow={(_, idx) => ({ onClick: () => setSelectedDetailIdx(idx ?? 0), style: { cursor: 'pointer', background: idx === selectedDetailIdx ? '#e6f4ff' : undefined } })} />
              {/* Detail sub-fields for selected row */}
              {details.length > 0 && (
                <div className="mt-3 p-3 border rounded bg-gray-50">
                  <div className="text-xs font-bold text-gray-600 mb-2">項次 {selectedDetail.fd111_02 ?? '-'} 明細資訊</div>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label data-contract-field="fd111_29" className="block text-xs text-gray-600 mb-1">ASSORT</label>
                      <Select value={selectedDetail.fd111_29 || 'N'} disabled={!isEditing} style={{width:'100%'}} size="small"
                        onChange={v => updateDetailField(selectedDetailIdx,'fd111_29',v)}
                        options={[{value:'Y',label:'Y'},{value:'N',label:'N'}]} />
                    </div>
                    <div className="col-span-2">
                      <label data-contract-field="fd111_51" className="block text-xs text-gray-600 mb-1">產品組織/規格</label>
                      <TextArea value={selectedDetail.fd111_51||''} disabled={!isEditing} rows={2}
                        onChange={e => updateDetailField(selectedDetailIdx,'fd111_51',e.target.value)} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_52" className="block text-xs text-gray-600 mb-1">加工別</label>
                      <TextArea value={selectedDetail.fd111_52||''} disabled={!isEditing} rows={2}
                        onChange={e => updateDetailField(selectedDetailIdx,'fd111_52',e.target.value)} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_53" className="block text-xs text-gray-600 mb-1">針孔內</label>
                      <Input value={selectedDetail.fd111_53||''} disabled={!isEditing} size="small"
                        onChange={e => updateDetailField(selectedDetailIdx,'fd111_53',e.target.value)} maxLength={10} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_54" className="block text-xs text-gray-600 mb-1">針孔外</label>
                      <Input value={selectedDetail.fd111_54||''} disabled={!isEditing} size="small"
                        onChange={e => updateDetailField(selectedDetailIdx,'fd111_54',e.target.value)} maxLength={10} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_55" className="block text-xs text-gray-600 mb-1">碼重</label>
                      <Input value={selectedDetail.fd111_55||''} disabled={!isEditing} size="small"
                        onChange={e => updateDetailField(selectedDetailIdx,'fd111_55',e.target.value)} maxLength={10} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_56" className="block text-xs text-gray-600 mb-1">米平方重</label>
                      <Input value={selectedDetail.fd111_56||''} disabled={!isEditing} size="small"
                        onChange={e => updateDetailField(selectedDetailIdx,'fd111_56',e.target.value)} maxLength={10} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_30" className="block text-xs text-gray-600 mb-1">允收量</label>
                      <InputNumber value={selectedDetail.fd111_30??0} disabled style={{width:'100%'}} size="small" />
                    </div>
                    <div>
                      <label data-contract-field="fd111_301" className="block text-xs text-gray-600 mb-1">允收比率</label>
                      <InputNumber value={selectedDetail.fd111_301??0} disabled={!isEditing} style={{width:'100%'}} size="small"
                        onChange={n => updateDetailField(selectedDetailIdx,'fd111_301',n??0)} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_302" className="block text-xs text-gray-600 mb-1">允收增加量</label>
                      <InputNumber value={selectedDetail.fd111_302??0} disabled={!isEditing} style={{width:'100%'}} size="small"
                        onChange={n => updateDetailField(selectedDetailIdx,'fd111_302',n??0)} />
                    </div>
                    <div>
                      <label data-contract-field="fd111_12" className="block text-xs text-gray-600 mb-1">產品名稱</label>
                      <TextArea value={selectedDetail.fd111_12||''} disabled={!isEditing} rows={2}
                        onChange={e => updateDetailField(selectedDetailIdx,'fd111_12',e.target.value)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )},
          { key:'3', label:'備註/彙總', children: renderTab3(formData,current,ntdTotal,isEditing,updateField) },
        ]} />
        {current && mode==='browse' && (
          <div className="text-xs text-gray-400 mt-3">
            建檔人員: {current.fd110_91} ({lookupNames.entryPerson||''})
            {' | '}建檔日期: {current.fd110_48 ? dayjs(current.fd110_48).format('YYYY-MM-DD') : '-'}
            {' | '}修改日期: {current.fd110_92 ? dayjs(current.fd110_92).format('YYYY-MM-DD') : '-'}
          </div>
        )}
        {/* Spacer for fixed bottom bar */}
        <div style={{ height: 64 }} />
        <SCSelectModal open={showSCModal} onCancel={() => setShowSCModal(false)} onSelect={importFromSC} />

        {/* Print Options Modal */}
        <PrintOptionsModal
          visible={printModalVisible}
          poNo={current?.fd110_01 || ''}
          onClose={() => setPrintModalVisible(false)}
        />

        {/* History Modal */}
        <Modal
          title="審核歷史"
          open={historyVisible}
          onCancel={() => setHistoryVisible(false)}
          footer={<Button onClick={() => setHistoryVisible(false)}>關閉</Button>}
          width={480}
        >
          {historyLoading ? (
            <Spin />
          ) : historyEvents.length === 0 ? (
            <p style={{ color: '#999' }}>尚無審核歷史記錄</p>
          ) : (
            <Timeline
              items={historyEvents.map(ev => ({
                color: ev.action === '核准' ? 'green' : ev.action === '退回' ? 'red' : 'blue',
                children: (
                  <div>
                    <strong>{ev.action}</strong>
                    {ev.user && <span style={{ marginLeft: 8, color: '#666' }}>by {ev.user}</span>}
                    {ev.date && (
                      <span style={{ marginLeft: 8, color: '#999' }}>
                        {dayjs(ev.date).format('YYYY-MM-DD HH:mm')}
                      </span>
                    )}
                    {ev.note && <div style={{ color: '#555', marginTop: 2 }}>{ev.note}</div>}
                  </div>
                ),
              }))}
            />
          )}
        </Modal>

        {/* ===== Fixed Bottom Action Bar (SharedActionBar) ===== */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!current}
          currentIdx={currentIdx}
          recordCount={records.length}
          onNavFirst={navFirst}
          onNavPrev={navPrev}
          onNavNext={navNext}
          onNavLast={navLast}
          onAdd={startAdd}
          onModify={startEdit}
          onDelete={handleDelete}
          onSave={save}
          onCancel={cancel}
          approval={{
            statusCode: (current?.fd110_80 || '').trim(),
            isApproved: (current?.fd110_80 || '').trim() === 'P',
            isPending: ['S', 'D'].includes((current?.fd110_80 || '').trim()), // 新碼 S + 舊碼 D
            isLocked: ['P', 'S', 'D'].includes((current?.fd110_80 || '').trim()),
          }}
          onSubmit={handleSubmitClick}
          onApprove={handleApproveClick}
          onReject={handleRejectClick}
          onWriteoff={handleWriteoffClick}
          onHistory={handleShowHistory}
          onPrint={handlePrint}
          extraEditButtons={
            mode === 'add' ? <button className="sab-btn" onClick={() => setShowSCModal(true)}>從 SC 帶入</button> : undefined
          }
          onExit={() => navigate('/erp')}
          canEdit={canEdit}
          canApprove={canApprove}
          acceptanceActionIds={{
            list: 'PWD201F1-ACT-LIST',
            get: 'PWD201F1-ACT-GET',
            create: 'PWD201F1-ACT-CREATE',
            update: 'PWD201F1-ACT-UPDATE',
            delete: 'PWD201F1-ACT-DELETE',
            submit: 'PWD201F1-ACT-SUBMIT',
            approve: 'PWD201F1-ACT-APPROVE',
            reject: 'PWD201F1-ACT-REJECT',
            writeoff: 'PWD201F1-ACT-WRITEOFF',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwd201f1AcceptanceManifest}
          records={records}
          selectedKeys={current?.fd110_01 ? [current.fd110_01] : []}
          keyOf={(record) => record.fd110_01}
          queryExact={(key) => d201Api.get(key)}
          lastApiResponse={lastApiResponse}
          loading={loading}
          onAutoFill={navFirst}
          autoFillDisabled={records.length === 0}
          autoFillReason="載入正式 API 已存在的第一筆採購單作為範例；不直接寫資料庫，儲存與審核仍走原表單 API。"
          screenDraft={{ master: formData, details }}
        />
      </div>
    </Spin>
  );
};

function renderTab1(
  fd: Partial<FD110>, ed: boolean, ln: Record<string,string>,
  uf: (f:string,v:unknown)=>void, rsn: (k:string,t:string,c:string)=>void,
  ptOpts: {value:string;label:string}[], mode: Mode,
  getFieldError: (field: string) => string | undefined,
) {
  const E = (field: string) => {
    const err = getFieldError(field);
    return err ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>{err}</span> : null;
  };

  return (
    <div className="grid grid-cols-4 gap-3">
      <div>
        <label data-contract-field="fd110_01" className="block text-xs text-gray-600 mb-1">採購單號</label>
        <Input value={fd.fd110_01||(mode==='add'?'(自動產生)':'')} disabled />
      </div>
      <div>
        <label data-contract-field="fd110_10" className="block text-xs text-gray-600 mb-1">S/C NO{E('fd110_10')}</label>
        <Input value={fd.fd110_10||''} disabled={!ed} onChange={e => uf('fd110_10',e.target.value)} maxLength={8} />
      </div>
      <div>
        <label data-contract-field="fd110_11" className="block text-xs text-gray-600 mb-1">採購日期{E('fd110_11')}</label>
        <DatePicker value={dateToInput(fd.fd110_11)} disabled={!ed}
          onChange={(_d,ds) => uf('fd110_11',ds?`${ds}T00:00:00Z`:null)} style={{width:'100%'}} />
      </div>
      <div>
        <label data-contract-field="fd110_28" className="block text-xs text-gray-600 mb-1">購貨類別{E('fd110_28')}</label>
        <Select value={fd.fd110_28||'1'} onChange={v => uf('fd110_28',v)} disabled={!ed} options={ptOpts} style={{width:'100%'}} />
      </div>
      <div>
        <LookupSelect lookupType="customer" label="分包商代號" contractFieldId="fd110_12" value={fd.fd110_12||''} disabled={!ed}
          onChange={v => uf('fd110_12',v)} onNameResolved={n => { ln.supplier = n; }} />
        {E('fd110_12')}
      </div>
      <LkField label="部門代號" contractFieldId="fd110_18" value={fd.fd110_18||''} name={ln.department||''} disabled={!ed} maxLen={5}
        onChange={v => uf('fd110_18',v)} onBlur={() => rsn('department','department',fd.fd110_18||'')} />
      <div>
        <LkField label="幣別" contractFieldId="fd110_19" value={fd.fd110_19||''} name={ln.currency||''} disabled={!ed} maxLen={5}
          onChange={v => uf('fd110_19',v)} onBlur={() => rsn('currency','currency',fd.fd110_19||'')} />
        {E('fd110_19')}
      </div>
      <div>
        <label data-contract-field="fd110_20" className="block text-xs text-gray-600 mb-1">匯率{E('fd110_20')}</label>
        <InputNumber value={fd.fd110_20??1} disabled={!ed} onChange={v => uf('fd110_20',v)} precision={4} style={{width:'100%'}} />
      </div>
      <div>
        <LkField label="交易條件" contractFieldId="fd110_141" value={fd.fd110_141||''} name={ln.tradeTerms||''} disabled={!ed} maxLen={5}
          onChange={v => uf('fd110_141',v)} onBlur={() => rsn('tradeTerms','tradeTerms',fd.fd110_141||'')} />
        {E('fd110_141')}
      </div>
      <div>
        <LkField label="付款條件" contractFieldId="fd110_151" value={fd.fd110_151||''} name={ln.paymentTerms||''} disabled={!ed} maxLen={5}
          onChange={v => uf('fd110_151',v)} onBlur={() => rsn('paymentTerms','paymentTerms',fd.fd110_151||'')} />
        {E('fd110_151')}
      </div>
      <div>
        <LkField label="包裝方式" contractFieldId="fd110_16" value={fd.fd110_16||''} name={ln.packing||''} disabled={!ed} maxLen={5}
          onChange={v => uf('fd110_16',v)} onBlur={() => rsn('packing','packing',fd.fd110_16||'')} />
        {E('fd110_16')}
      </div>
      <div>
        <LkField label="收貨公司" contractFieldId="fd110_33" value={fd.fd110_33||''} name={ln.receiverCompany||''} disabled={!ed}
          onChange={v => uf('fd110_33',v)} onBlur={() => rsn('receiverCompany','customer',fd.fd110_33||'')} />
        {E('fd110_33')}
      </div>
      <LkField label="業務員" contractFieldId="fd110_17" value={fd.fd110_17||''} name={ln.salesperson||''} disabled={!ed} maxLen={5}
        onChange={v => uf('fd110_17',v)} onBlur={() => rsn('salesperson','employee',fd.fd110_17||'')} />
      <div>
        <LookupSelect lookupType="employee" label="採購人員" contractFieldId="fd110_31" value={fd.fd110_31||''} disabled={!ed}
          onChange={v => uf('fd110_31',v)} onNameResolved={n => { ln.purchaser = n; }} />
        {E('fd110_31')}
      </div>
      <div>
        <LookupSelect lookupType="employee" label="QC人員" contractFieldId="fd110_30" value={fd.fd110_30||''} disabled={!ed}
          onChange={v => uf('fd110_30',v)} onNameResolved={n => { ln.qcPerson = n; }} />
        {E('fd110_30')}
      </div>
      <div className="flex items-end pb-1">
          <Checkbox data-contract-field="fd110_55" checked={fd.fd110_55||false} disabled={!ed} onChange={e => uf('fd110_55',e.target.checked)}>三聯式發票</Checkbox>
      </div>
      <div className="col-span-2">
        <label data-contract-field="fd110_13" className="block text-xs text-gray-600 mb-1">收貨地址</label>
        <Input value={fd.fd110_13||''} disabled={!ed} onChange={e => uf('fd110_13',e.target.value)} maxLength={60} />
      </div>
      <div>
        <label data-contract-field="fd110_161" className="block text-xs text-gray-600 mb-1">包裝方式說明</label>
        <Input value={fd.fd110_161||''} disabled />
      </div>
      <div>
        <label data-contract-field="fd110_29" className="block text-xs text-gray-600 mb-1">結案狀態</label>
        <Input value={fd.fd110_29==='P'?'已結案':'未結案'} disabled />
      </div>
    </div>
  );
}

function renderTab3(fd: Partial<FD110>, cur: FD110|null, ntdTotal: number, ed: boolean, uf: (f:string,v:unknown)=>void) {
  const s = cur || fd;
  const items: [string,string,string][] = [
    ['原幣總金額',fmt(s.fd110_23),'fd110_23'], ['台幣/人民幣總金額',fmt(s.fd110_24),'fd110_24'],
    ['台幣總金額 (計算)',fmt(Math.round(ntdTotal)),'ui_ntd_total'], ['匯率',(s.fd110_20??1).toFixed(4),'fd110_20'],
    ['幣別',s.fd110_19||'','fd110_19'], ['明細項數',String(s.fd110_43??0),'fd110_43'],
    ['版次',String(s.fd110_90??0),'fd110_90'],
    ['審核狀態',s.fd110_80==='P'?'已核准':s.fd110_80==='D'?'送審中':s.fd110_80==='N'?'已沖銷':'草稿','fd110_80'],
    ['核准日期',s.fd110_47?dayjs(s.fd110_47).format('YYYY-MM-DD'):'-','fd110_47'],
  ];
  return (
    <div>
      {/* 備註 / 品質要求 (from FoxPro Page3) */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="col-span-4">
          <label data-contract-field="fd110_41" className="block text-xs text-gray-600 mb-1">SHIPPING MARK</label>
          <Input.TextArea value={fd.fd110_41||''} disabled={!ed} rows={3} onChange={e => uf('fd110_41',e.target.value)} maxLength={500} />
        </div>
        <div>
          <label data-contract-field="fd110_45" className="block text-xs text-gray-600 mb-1">樣布需要量</label>
          <Input value={fd.fd110_45||''} disabled={!ed} onChange={e => uf('fd110_45',e.target.value)} maxLength={2} />
        </div>
        <div className="col-span-3 flex items-end">
          <Checkbox data-contract-field="fd110_54" checked={fd.fd110_54||false} disabled={!ed} onChange={e => uf('fd110_54',e.target.checked)}>內部交易</Checkbox>
        </div>
        <div>
          <label data-contract-field="fd110_421" className="block text-xs text-gray-600 mb-1">備註代號</label>
          <Input value={fd.fd110_421||''} disabled={!ed} onChange={e => uf('fd110_421',e.target.value)} maxLength={5} />
        </div>
        <div className="col-span-3">
          <label data-contract-field="fd110_42" className="block text-xs text-gray-600 mb-1">備註說明</label>
          <Input.TextArea value={fd.fd110_42||''} disabled={!ed} rows={2} onChange={e => uf('fd110_42',e.target.value)} maxLength={200} />
        </div>
        <div>
          <label data-contract-field="fd110_44" className="block text-xs text-gray-600 mb-1">品質要求代號</label>
          <Input value={fd.fd110_44||''} disabled={!ed} onChange={e => uf('fd110_44',e.target.value)} maxLength={5} />
        </div>
        <div className="col-span-3">
          <label data-contract-field="fd110_441" className="block text-xs text-gray-600 mb-1">品質要求說明</label>
          <Input.TextArea value={fd.fd110_441||''} disabled={!ed} rows={2} onChange={e => uf('fd110_441',e.target.value)} maxLength={500} />
        </div>
      </div>
      {/* 彙總 */}
      <div className="grid grid-cols-3 gap-3">
        {items.map(([l,v,fieldId]) => <div key={l}><label data-contract-field={fieldId} className="text-xs text-gray-600">{l}</label><Input value={v} disabled /></div>)}
      </div>
    </div>
  );
}

export default D201Form;
